import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IssueNotice, LANGUAGES, NoticeSent } from '../../api/issues';

export interface IIssueNoticePanelProps {
    notice: IssueNotice | null;
    loading: boolean;
    error: string | null;
    all: boolean;
    marking: boolean;
    onAllChange: (all: boolean) => void;
    onMarkSent: (notice: IssueNotice, sent: NoticeSent) => void;
}

/**
 * The message to the customer of the selected environment about everything that waits on
 * them, in their language and our house style: to read, adjust here, copy into the mail or
 * Freshdesk, and send by hand. Marking it as sent keeps the same issues out of the next
 * message, and keeps the text as it went out to learn the house style from.
 */
export const IssueNoticePanel = ({ notice, loading, error, all, marking, onAllChange, onMarkSent }: IIssueNoticePanelProps) => {
    const [copied, setCopied] = useState<string | null>(null);
    const [subject, setSubject] = useState<string>('');
    const [body, setBody] = useState<string>('');

    // A new proposal replaces whatever was being edited.
    useEffect(() => {
        setSubject(notice?.subject ?? '');
        setBody(notice?.body ?? '');
    }, [notice]);

    const copy = (what: string, text: string) => {
        navigator.clipboard
            ?.writeText(text)
            .then(() => {
                setCopied(what);
                window.setTimeout(() => setCopied(null), 2000);
            })
            .catch(() => setCopied(null));
    };

    const edited = notice !== null && (subject !== (notice.subject ?? '') || body !== (notice.body ?? ''));

    return (
        <section className="issues__notice" aria-label="Klant informeren">
            <div className="issues__notice-head">
                <h3 className="issues__heading">
                    <FontAwesomeIcon icon="envelope" /> Klant informeren
                    {notice && (
                        <span className="issues__muted">
                            {notice.customer} / {notice.environment}, mail in het {notice.language === 'nl' ? 'Nederlands' : 'Engels'}
                            {notice.customerLanguage !== notice.language &&
                                ` (klant: ${LANGUAGES[notice.customerLanguage]?.replace(' (mail Engels)', '') ?? notice.customerLanguage})`}
                        </span>
                    )}
                </h3>
                <label className="form-check-label">
                    <input type="checkbox" className="form-check-input me-1" checked={all} onChange={(event) => onAllChange(event.target.checked)} />
                    Ook wat de klant al weet
                </label>
            </div>

            {loading && <p className="issues__muted">Bericht opstellen…</p>}
            {error && <p className="issues__error">{error}</p>}

            {notice && !loading && !notice.needed && (
                <p className="issues__muted">
                    <FontAwesomeIcon icon="circle-check" /> {notice.reason}
                </p>
            )}

            {notice && !loading && notice.needed && (
                <>
                    <p className="issues__muted">
                        {notice.reason} Lees de tekst na en pas hem hier aan als dat nodig is; wat je verandert, leert de huisstijl.
                        {notice.withoutText > 0 && ` ${notice.withoutText} punt(en) hebben nog geen tekst voor de klant en staan er niet in.`}
                    </p>

                    {notice.emailAddress && (
                        <div className="issues__copy-row">
                            <input className="form-control" readOnly value={notice.emailAddress} aria-label="Aan" />
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copy('to', notice.emailAddress ?? '')}>
                                <FontAwesomeIcon icon="copy" /> {copied === 'to' ? 'Gekopieerd' : 'Kopiëren'}
                            </button>
                        </div>
                    )}

                    <div className="issues__copy-row">
                        <input className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Onderwerp" />
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copy('subject', subject)}>
                            <FontAwesomeIcon icon="copy" /> {copied === 'subject' ? 'Gekopieerd' : 'Kopiëren'}
                        </button>
                    </div>

                    <textarea
                        className="form-control issues__body"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={Math.min(22, body.split('\n').length + 2)}
                        aria-label="Tekst"
                    />

                    <div className="issues__actions">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copy('body', body)}>
                            <FontAwesomeIcon icon="copy" /> {copied === 'body' ? 'Gekopieerd' : 'Tekst kopiëren'}
                        </button>
                        {notice.emailAddress && (
                            <a
                                className="btn btn-sm btn-outline-secondary"
                                href={`mailto:${encodeURIComponent(notice.emailAddress)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
                            >
                                <FontAwesomeIcon icon="paper-plane" /> Open in mail
                            </a>
                        )}
                        {edited && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                    setSubject(notice.subject ?? '');
                                    setBody(notice.body ?? '');
                                }}
                            >
                                <FontAwesomeIcon icon="rotate-left" /> Voorstel terug
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={marking || notice.keys.length === 0}
                            onClick={() =>
                                onMarkSent(notice, {
                                    keys: notice.keys,
                                    language: notice.language,
                                    subject,
                                    proposed: notice.body,
                                    sent: body,
                                })
                            }
                            title="Onthoudt dat de klant van deze punten weet, en bewaart de tekst zoals hij verstuurd is"
                        >
                            <FontAwesomeIcon icon="check" /> {marking ? 'Bezig…' : 'Markeer als verstuurd'}
                        </button>
                    </div>
                </>
            )}
        </section>
    );
};
